import PersonalDefaultPage from "./PersonalDefaultPage";
import { useSelector, useDispatch } from "react-redux";
import { useWindowDimensions } from "../../utils";
import { useEffect, useCallback, useState, useMemo } from "react";
import { get_user } from "../../components/blocks/profileUpdating/dataUpdating/ProfileDataUpdatingBlock";
import { setUser } from "../../store/UserSlice";
import Config from "../../Config";
import axios from "axios";
import { BsChevronDown } from "react-icons/bs";
import { setLibraryFiles } from "../../store/LibraryFilesSlice";
import UpdateCardView from "../../components/blocks/Updates/UpdateCardView";
import UpdatesAdminForm from "../../components/blocks/Updates/UpdatesAdminForm";
import UpdatesAdminListItem from "../../components/blocks/Updates/UpdatesAdminListItem";
import { UPDATES_PAGE_SIZE } from "../../components/blocks/Updates/updatesConstants";
import {
  AdminExitButton,
  AdminModeBar,
  AdminModeToggle,
  EmptyState,
  FeedList,
  LoadMoreButton,
  LoadMoreWrap,
  UpdatesHeader,
  UpdatesHeading,
  UpdatesShell,
  UpdatesSubtitle,
} from "../../components/blocks/Updates/UpdatesPage.styles";
import { useI18n } from "../../i18n/I18nProvider";
import { isAdminUser } from "../../constants/adminAccess";

export const Updates = () => {
  const { t } = useI18n();
  const { height: screenHeight, width: screenWidth } = useWindowDimensions();
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const [changes, setChanges] = useState([]);
  const [visibleCount, setVisibleCount] = useState(UPDATES_PAGE_SIZE);
  const [hasMoreRemote, setHasMoreRemote] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [usesPagedApi, setUsesPagedApi] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isAdminView, setIsAdminView] = useState(false);
  const [publishWaiting, setPublishWaiting] = useState(false);
  const isAdmin = isAdminUser(user.id);
  const compactViewport =
    screenWidth <= 650 || (screenHeight <= 500 && screenWidth <= 950);
  const showTimeline = !compactViewport;

  useEffect(() => {
    (async () => {
      if (!user || Object.keys(user).length === 0) {
        try {
          const data = await get_user();
          dispatch(setUser(data));
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      }
    })();
  }, [dispatch, user]);

  const fetchChanges = useCallback(async () => {
    try {
      const response = await axios.get(`${Config.BACKEND_ADDR}/changes/`, {
        params: { limit: UPDATES_PAGE_SIZE },
      });
      if (response.status === 200) {
        if (Array.isArray(response.data)) {
          // Backward-compatible until the paginated backend is deployed.
          setChanges(response.data);
          setUsesPagedApi(false);
          setHasMoreRemote(false);
        } else {
          setChanges(response.data.items || []);
          setUsesPagedApi(true);
          setHasMoreRemote(Boolean(response.data.has_more));
          setNextCursor(response.data.next_cursor || null);
        }
        setVisibleCount(UPDATES_PAGE_SIZE);
      }
    } catch (error) {
      console.error("Error fetching changes:", error);
    }
    try {
      const response = await axios.get(`${Config.BACKEND_ADDR}/library/`);
      if (response.status === 200) {
        dispatch(setLibraryFiles(response.data));
      }
    } catch (error) {
      console.error("Error fetching library files:", error);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchChanges();
  }, [fetchChanges]);

  const fetchAllChanges = useCallback(async () => {
    try {
      const response = await axios.get(`${Config.BACKEND_ADDR}/changes/`);
      if (response.status === 200) {
        setChanges(
          Array.isArray(response.data) ? response.data : response.data.items || [],
        );
        setUsesPagedApi(false);
        setHasMoreRemote(false);
        setVisibleCount(Number.MAX_SAFE_INTEGER);
      }
    } catch (error) {
      console.error("Error fetching all changes:", error);
    }
  }, []);

  const handleToggleAdmin = useCallback(() => {
    if (isAdminView) {
      setIsAdminView(false);
      fetchChanges();
      return;
    }
    setIsAdminView(true);
    fetchAllChanges();
  }, [fetchAllChanges, fetchChanges, isAdminView]);

  const handleLoadMore = useCallback(async () => {
    if (!usesPagedApi) {
      setVisibleCount((count) => count + UPDATES_PAGE_SIZE);
      return;
    }
    if (!hasMoreRemote || !nextCursor || loadingMore) return;

    setLoadingMore(true);
    try {
      const response = await axios.get(`${Config.BACKEND_ADDR}/changes/`, {
        params: { limit: UPDATES_PAGE_SIZE, before_id: nextCursor },
      });
      if (response.status === 200) {
        const page = response.data;
        setChanges((current) => [...current, ...(page.items || [])]);
        setHasMoreRemote(Boolean(page.has_more));
        setNextCursor(page.next_cursor || null);
      }
    } catch (error) {
      console.error("Error loading more changes:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMoreRemote, loadingMore, nextCursor, usesPagedApi]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!user || Object.keys(user).length === 0) return;
      if (user.unread_changes_count === 0) return;
      dispatch(setUser({ ...user, unread_changes_count: 0 }));
    }, 3000);
    return () => clearTimeout(timer);
  }, [dispatch, user]);

  const sortedChanges = useMemo(
    () =>
      [...changes].sort(
        (a, b) => new Date(b.time_create) - new Date(a.time_create),
      ),
    [changes],
  );

  const visibleChanges = usesPagedApi
    ? sortedChanges
    : sortedChanges.slice(0, visibleCount);
  const hasMore = usesPagedApi
    ? hasMoreRemote
    : visibleCount < sortedChanges.length;

  const handlePublish = useCallback(
    async (payload) => {
      setPublishWaiting(true);
      try {
        const response = await axios.put(`${Config.BACKEND_ADDR}/changes/`, {
          type: payload.type,
          address: payload.address,
          text: payload.text,
          text_en: payload.text_en || "",
        });
        if (response.status === 200) {
          await fetchAllChanges();
        }
      } catch (error) {
        console.error("Error creating update:", error);
        alert(t("Не удалось опубликовать новость"));
      } finally {
        setPublishWaiting(false);
      }
    },
    [fetchAllChanges, t],
  );

  return (
    <PersonalDefaultPage>
      <UpdatesShell $compact={screenWidth <= 1366}>
        <UpdatesHeader>
          <UpdatesHeading>{t("Обновления")}</UpdatesHeading>
          <UpdatesSubtitle>
            {t("Всё новое в SPEAKID — в одном месте")}
          </UpdatesSubtitle>
        </UpdatesHeader>

        {isAdmin && (
          <AdminModeBar>
            <AdminModeToggle
              type="button"
              $active={isAdminView}
              onClick={handleToggleAdmin}
            >
              {isAdminView ? t("Режим администратора") : t("Войти в режим админа")}
            </AdminModeToggle>
            {isAdminView && (
              <AdminExitButton type="button" onClick={handleToggleAdmin}>
                {t("Выйти из режима")}
              </AdminExitButton>
            )}
          </AdminModeBar>
        )}

        {isAdminView ? (
          <>
            <UpdatesAdminForm
              mode="create"
              onSubmit={handlePublish}
              isWaiting={publishWaiting}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {sortedChanges.map((change) => (
                <UpdatesAdminListItem
                  key={change.id}
                  change={change}
                  onFetchChanges={fetchAllChanges}
                />
              ))}
            </div>
          </>
        ) : sortedChanges.length > 0 ? (
          <>
            <FeedList $timeline={showTimeline}>
              {visibleChanges.map((change, index) => (
                <UpdateCardView
                  key={change.id}
                  change={change}
                  featured={index === 0}
                  showTimeline={showTimeline}
                />
              ))}
            </FeedList>
            {hasMore && (
              <LoadMoreWrap>
                <LoadMoreButton
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    t("Загрузка…")
                  ) : (
                    <>
                      {t("Показать ещё")} <BsChevronDown aria-hidden="true" />
                    </>
                  )}
                </LoadMoreButton>
              </LoadMoreWrap>
            )}
          </>
        ) : (
          <EmptyState>{t("Скоро тут что-то будет")}</EmptyState>
        )}
      </UpdatesShell>
    </PersonalDefaultPage>
  );
};
